import React, { useState, useCallback, useMemo, useEffect } from "react";
import { getDisplayName } from "data/constants";
import { usePersistSetting } from "hooks";
import { produce } from "immer";
import {
    type NewFormTypeEnum,
    type DocumentFormDTO,
    NewFormType,
} from "data/types";
import { getLocalizedText } from "data/utils";
import Control from "./Control";

type Props = {
    newFormType: NewFormTypeEnum,
    form: DocumentFormDTO,
};

export type WebDavNameProps = {
    ignore: boolean,
    renderControl: (Array<string>) => any,
};

const withWebDavName = (WrappedComponent: ComponentType<any>) => {
    const HOC = (props: Props) => {
        const [keep, setKeep] = usePersistSetting({
            name: "keepFilename",
            defaultValue: true,
        });
        const isNewDocument = props.newFormType === NewFormType.Document;
        const computedKeep = keep && isNewDocument;
        const [form, setForm] = useState(props.form);

        useEffect(() => {
            setForm(props.form);
        }, [props.form]);

        useEffect(() => {
            if (!isNewDocument) {
                return;
            }
            setForm((form) =>
                produce(form, (draft) => {
                    const idx =
                        draft?.fields?.findIndex(
                            (f) =>
                                f.format === "WebDavName" && f.name === "$NAME$"
                        ) ?? -1;
                    /* istanbul ignore else */
                    if (idx > -1) {
                        draft.fields[idx].ro = computedKeep;
                    }
                })
            );
        }, [isNewDocument, computedKeep]);

        // render control
        const renderControl = useCallback(
            (classNames = []) =>
                isNewDocument && (
                    <Control
                        classNames={classNames}
                        keep={keep}
                        setKeep={setKeep}
                    />
                ),
            [isNewDocument, keep, setKeep]
        );

        const isWebDavNameHidden =
            props.newFormType != null &&
            (form?.fields?.some(
                (f) =>
                    f.format === "WebDavName" &&
                    f.name === "$NAME$" &&
                    f.extra &&
                    f.extra.hidden
            ) ??
                false);

        const ignoreWebDavName = computedKeep || isWebDavNameHidden;

        const defaultValues = useMemo(() => {
            if (!isWebDavNameHidden) {
                return props.defaultValues;
            }
            return produce(props.defaultValues || {}, (draft: ?Object) => {
                /* istanbul ignore else */
                if (!draft["$NAME$"]) {
                    draft["$NAME$"] = getLocalizedText("$common:new_folder");
                }
                return draft;
            });
        }, [isWebDavNameHidden, props.defaultValues]);

        // create additional props
        const additionalProps = useMemo(
            () => ({
                renderControl,
                ignore: ignoreWebDavName,
            }),
            [renderControl, ignoreWebDavName]
        );

        return (
            <WrappedComponent
                {...props}
                webDavName={additionalProps}
                form={form}
                defaultValues={defaultValues}
            />
        );
    };
    HOC.displayName = `withWebDavName(${getDisplayName(WrappedComponent)})`;
    return HOC;
};

export default withWebDavName;
